/** Document-text sanitation shared by the Word/Excel/PowerPoint hosts.
 *  LLMs frequently emit "single string with \n" content even when the tool
 *  schema asks for one paragraph per call — these helpers turn such text into
 *  host-native content (real paragraphs, no Markdown debris) as a product-level
 *  fallback on top of the prompt-level rules. */

/** Undo literal escape sequences ("\n", "\r\n", "\t") that models sometimes
 *  put inside JSON tool args — they would otherwise land in the document as
 *  the two characters '\' + 'n'. Real newline characters pass through. */
export function unescapeLiteralText(s: string): string {
  return String(s ?? "")
    .replace(/\\r\\n/g, "\n")
    .replace(/\\n/g, "\n")
    .replace(/\\t/g, "\t");
}

export interface ParagraphLine {
  text: string;
  /** Style hinted by a Markdown heading ("#" prefixes), used only when the
   *  operation itself carries no explicit style. */
  style?: "heading1" | "heading2" | "heading3";
}

/** Normalize a model-provided string into document-ready paragraph lines:
 *  unescape literal escapes, split on newlines, and strip Markdown
 *  decorations (heading marks, bullets, bold/italic/code markers) that would
 *  otherwise show up as raw characters in the document. Empty lines are
 *  dropped — paragraph structure already provides the spacing. */
export function toParagraphLines(raw: string): ParagraphLine[] {
  const out: ParagraphLine[] = [];
  for (const rawLine of unescapeLiteralText(raw).split(/\r?\n/)) {
    let line = rawLine.trim();
    if (!line) continue;
    let style: ParagraphLine["style"];
    const heading = line.match(/^(#{1,6})\s+(.*)$/);
    if (heading) {
      const level = heading[1].length;
      style = level <= 1 ? "heading1" : level === 2 ? "heading2" : "heading3";
      line = heading[2].trim();
    } else {
      line = line.replace(/^[-*+]\s+/, "");
    }
    line = line
      .replace(/\*\*([^*]+)\*\*/g, "$1")
      .replace(/__([^_]+)__/g, "$1")
      .replace(/\*([^*]+)\*/g, "$1")
      .replace(/`([^`]*)`/g, "$1")
      .trim();
    if (line) out.push(style ? { text: line, style } : { text: line });
  }
  return out;
}

/** Flatten an array of model-provided bullets into single-line strings
 *  (each bullet that still contains newlines becomes several bullets). */
export function normalizeBulletList(v: unknown): string[] {
  if (!Array.isArray(v)) return [];
  const out: string[] = [];
  for (const b of v) {
    for (const ln of toParagraphLines(String(b ?? ""))) out.push(ln.text);
  }
  return out;
}
